import { z } from 'zod';
import { pluginSourceAdminSchema } from '../schemas/source.js';
import { syncJobAdminSchema } from '../schemas/sync-job.js';
import { idParamSchema, listQueryBase, multiFilter, paginatedResponse, paginationQuerySchema, } from './_shared.js';
/**
 * `GET /api/sources` — admin list of provisioned sources. Gate `teams:view`.
 * Emits the admin-shaped row (includes the rotation pair) — never exposed
 * via public catalogues.
 *
 * Optional `orphaned=true` filters to rows with `status='failed' AND
 * team_id IS NULL` — the residual orphan class left by failed provisioning
 * attempts that admins never retried. Most orphans are cleaned up by the
 * retry path in `POST /api/teams` (`reuseFailedSource` hard-deletes
 * sibling failed-orphan rows under a SELECT … FOR UPDATE); this filter
 * surfaces the leftover diagnostic surface for ad-hoc cleanup tooling.
 */
// `z.coerce.boolean()` is unsafe for query strings — the literal "false" is
// a non-empty string and coerces to `true`. Constrain to the exact pair the
// HTTP surface accepts and transform deterministically.
export const listSourcesQuerySchema = listQueryBase.extend({
    orphaned: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
    status: multiFilter(['provisioning', 'active', 'failed']),
});
export const exportSourcesQuerySchema = listSourcesQuerySchema.omit({ page: true, pageSize: true });
export const listSourcesResponseSchema = paginatedResponse(pluginSourceAdminSchema);
/** Re-export of the canonical id-param schema for naming clarity at call sites. */
export const sourceIdParamSchema = idParamSchema;
/**
 * Two-phase rotation per spec §7.5. `begin` writes a new secret to KV +
 * ADO + `webhook_secret_pending`; the operator observes deliveries during
 * a 5-minute window; `promote` swaps `webhook_secret_pending` →
 * `webhook_secret`.
 */
export const rotateWebhookSecretBodySchema = z
    .object({ phase: z.enum(['begin', 'promote']) })
    .strict();
/**
 * `begin` returns the observation-window length and the new KV secret
 * version (stored on the row); `promote` returns just the phase.
 * Modeled as a discriminated union so consumers exhaustively handle both
 * phases without surfacing optional fields that don't apply.
 */
export const rotateWebhookSecretResponseSchema = z.discriminatedUnion('phase', [
    z.object({
        phase: z.literal('begin'),
        observationWindowMinutes: z.number().int().min(1),
        secretVersion: z.string().min(1),
    }),
    z.object({ phase: z.literal('promote') }),
]);
/**
 * `GET /api/sources/:id` — admin detail of a single source. Spec §4.3, §7.5.
 * Reuses `pluginSourceAdminSchema` so secret values are NEVER projected; the
 * admin UI consumes `hasPendingSecret` to drive the rotation widget.
 */
export const getSourceParamsSchema = sourceIdParamSchema;
export const getSourceResponseSchema = pluginSourceAdminSchema;
/**
 * `GET /api/sources/:id/sync-jobs` — paginated history of webhook-driven
 * syncs for the admin webhook health panel. Spec §5.4. Powers operator
 * verification during the §7.5 rotation observation window.
 */
export const listSyncJobsParamsSchema = sourceIdParamSchema;
export const listSyncJobsQuerySchema = paginationQuerySchema;
export const listSyncJobsResponseSchema = paginatedResponse(syncJobAdminSchema);
/**
 * `POST /api/sources/:id/refresh-template` — opens a PR against the team
 * repo's `main` that lands every template-side change since provisioning.
 *
 * Non-destructive: paths that exist in the team repo but NOT in the
 * template are left untouched (preserves team-authored work). When the
 * template tree matches the team tree exactly, the route short-circuits
 * and no PR is opened — `prUrl` and `branch` are both `null` in that
 * case.
 *
 * Idempotency. The branch name is deterministic
 * (`template/refresh-${templateSha.slice(0, 10)}`) so repeated calls
 * against the same template SHA target the same branch. The service
 * preflights the team repo for an existing branch at that name: if
 * present (prior call already pushed the content), it skips the push
 * and proceeds straight to `createPullRequest`, whose 409 dup-PR
 * fallback returns the existing PR. This covers all three idempotency
 * cases — branch deleted, PR merged, PR still open.
 */
export const refreshTemplateParamsSchema = sourceIdParamSchema;
export const refreshTemplateResponseSchema = z.object({
    prUrl: z.string().url().nullable(),
    branch: z.string().min(1).nullable(),
    templateSha: z.string().min(1),
    changedFiles: z.number().int().min(0),
});
/**
 * `POST /api/sources/:id/reactivate` — admin inverse of the `reconcile-sync`
 * auto-retirement (`source.archived_repo_gone`). When a source's ADO repo was
 * deleted, the reconciler soft-deletes the source and yanks its plugins; once
 * the repo reappears, this endpoint restores it: clears `deleted_at`, flips
 * `status` back to `active`, and un-yanks the plugins that flow had yanked.
 *
 * Fail-closed: the service confirms the repo exists again via `getRepo` before
 * mutating — a still-missing repo returns 409 `REPO_STILL_GONE`. Scoped to
 * repo-gone retirements only (matched on the `provision_error` marker), so a
 * team-deleted source is never a candidate (→ 404). Gate: `teams:update`,
 * team-scoped (matches the rotate/refresh precedent).
 */
export const reactivateSourceParamsSchema = sourceIdParamSchema;
export const reactivateSourceResponseSchema = z.object({
    source: pluginSourceAdminSchema,
    unyankedPluginCount: z.number().int().min(0),
});
/**
 * `POST /api/sources/:id/register-hook` — admin backfill for sources whose
 * webhook registration was deferred by provision-team's step 6 soft-fail
 * (Task 2, ADR-0003). Re-runs the ADO service-hook registration; on success,
 * flips `plugin_sources.webhook_enabled = true` and emits a
 * `source.webhook_registered` audit row.
 *
 * Gate: `sources:admin` (super_admin + marketplace_admin only — backfill is
 * an operator escape hatch, not a team-scoped op).
 *
 * Returns the ADO subscription id so the caller can confirm registration
 * landed; the next reconcile-sync pass closes any SHA gap that accumulated
 * while the hook was missing.
 */
export const registerHookParamsSchema = sourceIdParamSchema;
/**
 * Post-T10/I6: discriminated union. Pre-T10 the route returned 200 on
 * success and 403 on the permission-denied path — but ADO's permission
 * grant is the whole reason this endpoint exists (it's the backfill
 * for sources whose UAI hadn't been granted `ServiceHook:EditSubscriptions`
 * at provision time). Permission-denied is therefore a STRUCTURAL
 * outcome of the operation, not an exceptional error — modelling it as
 * a 200 with a discriminator lets the UI render an actionable badge
 * from a typed outcome instead of string-matching an error code.
 *
 * The 403 status now means RBAC denial only (caller lacks
 * `sources:admin`); 404 means the source row is absent.
 */
export const registerHookResponseSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('registered'), subscriptionId: z.string().min(1) }),
    z.object({ kind: z.literal('permission_denied'), reason: z.string().min(1) }),
]);
/**
 * `POST /api/sources/:id/sync` — manual re-sync trigger. Resolves the
 * source repo's current HEAD and enqueues a `sync-repo` job for it, exactly
 * as a webhook push would — replacing the redis-cli "manually enqueue a
 * sync-repo job" runbook procedure with a first-class admin endpoint.
 *
 * Gate `teams:update` + team-scope assertion (matches the rotate/refresh
 * precedent). The jobId is uniquified per invocation so a manual trigger is
 * never deduped against a still-pending webhook-enqueued job for the same
 * SHA.
 */
export const syncSourceParamsSchema = sourceIdParamSchema;
export const syncSourceBodySchema = z.object({}).strict();
export const syncSourceResponseSchema = z.object({
    sourceId: z.string().uuid(),
    jobId: z.string().min(1),
    headSha: z.string().min(1),
});
//# sourceMappingURL=sources.js.map